import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type DataMode = 'demo' | 'live';
export type ScreenName = 'overview' | 'calendar' | 'resources' | 'settings';

interface ScreenDataModes {
  overview: DataMode;
  calendar: DataMode;
  resources: DataMode;
  settings: DataMode;
}

interface DataModeContextType {
  screenModes: ScreenDataModes;
  getScreenMode: (screen: ScreenName) => DataMode;
  setScreenMode: (screen: ScreenName, mode: DataMode) => void;
  toggleScreenMode: (screen: ScreenName) => void;
  isDemo: (screen: ScreenName) => boolean;
  isLive: (screen: ScreenName) => boolean;
  isModeLocked: boolean;
  liveOnly: boolean;
}

const STORAGE_KEY = 'eb-scheduler-data-modes';

const GLOBAL_MODE_KEY = 'eb-scheduler-global-data-mode';

const LOCKED_KEY = 'eb-scheduler-data-mode-locked';

const DataModeContext = createContext<DataModeContextType | undefined>(undefined);

function saveModesToStorage(modes: ScreenDataModes): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
  } catch (error) {
    console.error('Error saving data modes to storage:', error);
  }
}

export function DataModeProvider({ children, defaultMode = 'live', liveOnly = false }: { children: ReactNode; defaultMode?: DataMode; liveOnly?: boolean }) {
  const initialMode: DataMode = liveOnly ? 'live' : defaultMode;
  const isModeLocked = localStorage.getItem(LOCKED_KEY) === 'true';
  const lockedMode = isModeLocked
    ? (localStorage.getItem(GLOBAL_MODE_KEY) as DataMode) || initialMode
    : null;

  const [globalMode, setGlobalMode] = useState<DataMode>(liveOnly ? 'live' : (lockedMode || initialMode));
  const [screenModes, setScreenModes] = useState<ScreenDataModes>({
    overview: initialMode,
    calendar: initialMode,
    resources: initialMode,
    settings: initialMode,
  });

  useEffect(() => {
    saveModesToStorage(screenModes);
  }, [screenModes]);

  useEffect(() => {
    try {
      localStorage.setItem(GLOBAL_MODE_KEY, globalMode);
    } catch (error) {
      console.error('Error saving global data mode:', error);
    }
    setScreenModes({
      overview: globalMode,
      calendar: globalMode,
      resources: globalMode,
      settings: globalMode,
    });
  }, [globalMode]);

  const getScreenMode = useCallback((_screen: ScreenName): DataMode => {
    return liveOnly ? 'live' : globalMode;
  }, [globalMode, liveOnly]);

  const setScreenMode = useCallback((_screen: ScreenName, mode: DataMode) => {
    if (isModeLocked || liveOnly) return;
    setGlobalMode(mode);
  }, [isModeLocked, liveOnly]);

  const toggleScreenMode = useCallback((_screen: ScreenName) => {
    if (isModeLocked || liveOnly) return;
    setGlobalMode(prev => prev === 'demo' ? 'live' : 'demo');
  }, [isModeLocked, liveOnly]);

  const isDemo = useCallback((_screen: ScreenName): boolean => {
    return liveOnly ? false : globalMode === 'demo';
  }, [globalMode, liveOnly]);

  const isLive = useCallback((_screen: ScreenName): boolean => {
    return liveOnly ? true : globalMode === 'live';
  }, [globalMode, liveOnly]);

  const value: DataModeContextType = {
    screenModes,
    getScreenMode,
    setScreenMode,
    toggleScreenMode,
    isDemo,
    isLive,
    isModeLocked,
    liveOnly,
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = useContext(DataModeContext);
  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
}
